import React from "react";
import { ProgressSpinner } from "primereact/progressspinner";
import { useTenantBrandColors } from "../../../hooks/useTenantBrandColors";
import { useTenantOptions } from "../../../hooks/useTenantOptions";
import { useGetBoMeQuery } from "../../../store/services/api";

export const BenefitsMetricsLoadingOverlay: React.FC<{ visible: boolean }> = ({
  visible,
}) => {
  if (!visible) {
    return null;
  }

  return (
    <div
      className="absolute top-0 left-0 w-full h-full flex flex-column align-items-center justify-content-center border-round"
      style={{ backgroundColor: "rgba(255, 255, 255, 0.92)", zIndex: 10 }}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <ProgressSpinner style={{ width: "48px", height: "48px" }} strokeWidth="4" />
    </div>
  );
};

export const BenefitsMetricsRefreshingIndicator: React.FC<{ visible: boolean }> = ({
  visible,
}) => {
  if (!visible) {
    return null;
  }

  return (
    <span
      className="inline-flex align-items-center gap-2 text-color-secondary text-sm"
      role="status"
      aria-live="polite"
    >
      <ProgressSpinner style={{ width: "16px", height: "16px" }} strokeWidth="6" />
      Actualizando…
    </span>
  );
};

/**
 * Aclara sobre qué tenant se están leyendo las métricas. Solo para SUPER_ADMIN: el
 * TENANT_ADMIN opera siempre en su único tenant, así que el cartel sería ruido.
 * El tenant lo manda la pantalla (mismo valor que el topbar) para que se actualice
 * al instante al cambiar el selector.
 */
export const BenefitsMetricsTenantScopeHeader: React.FC<{
  tenantId: string | null;
}> = ({ tenantId }) => {
  const { data: me, isLoading } = useGetBoMeQuery();
  const { options } = useTenantOptions();
  const brandColors = useTenantBrandColors();

  if (isLoading || me?.role !== "SUPER_ADMIN") {
    return null;
  }

  const tenantLabel =
    tenantId === null
      ? "Todos los tenants"
      : options.find((option) => option.value === tenantId)?.label ?? tenantId;
  const brandColor = tenantId ? brandColors[tenantId] : undefined;

  return (
    <div className="flex justify-content-center mb-4">
      <div
        className={`inline-flex flex-wrap align-items-center justify-content-center gap-2 py-2 px-4 border-round-3xl${
          brandColor ? "" : " bg-primary-50"
        }`}
        style={brandColor ? { backgroundColor: `${brandColor}14` } : undefined}
        data-testid="benefits-metrics-tenant-scope"
      >
        <span
          className={`border-circle${brandColor ? "" : " bg-primary"}`}
          style={{
            width: "8px",
            height: "8px",
            ...(brandColor ? { backgroundColor: brandColor } : {}),
          }}
          aria-hidden="true"
        />
        <span className="text-lg text-color-secondary">Operando en</span>
        <span
          className={`text-lg font-semibold${brandColor ? "" : " text-primary"}`}
          style={brandColor ? { color: brandColor } : undefined}
        >
          {tenantLabel}
        </span>
      </div>
    </div>
  );
};

interface BenefitsMetricsKpiCardsProps {
  totalActiveBenefits?: number | null;
  totalRedemptions?: number | null;
  /**
   * Sin uso: la tasa de conversión dependía del tracking de vistas
   * (benefit_view), que se desactivó en la app porque solo se mide el canje.
   * Se mantiene la prop para no romper los call sites si se reactiva.
   */
  conversionRateLabel?: string | null;
}

const KpiCard: React.FC<{
  label: string;
  value: React.ReactNode;
  description: string;
}> = ({ label, value, description }) => (
  <div className="col-12 md:col-6">
    <div className="surface-100 border-round p-3 h-full flex flex-column">
      <div className="text-color-secondary mb-2">{label}</div>
      <div className="text-3xl font-bold text-900">{value}</div>
      <small
        className="text-color-secondary block mt-2"
        style={{ minHeight: "2.5rem" }}
      >
        {description}
      </small>
    </div>
  </div>
);

export const BenefitsMetricsKpiCards: React.FC<BenefitsMetricsKpiCardsProps> = ({
  totalActiveBenefits,
  totalRedemptions,
}) => (
  <div className="grid mb-4">
    <KpiCard
      label="Beneficios activos"
      value={totalActiveBenefits ?? "—"}
      description="Beneficios con estado activo en el alcance actual."
    />
    <KpiCard
      label="Canjes"
      value={totalRedemptions ?? "—"}
      description="Canjes registrados en el rango de fechas."
    />
    {/* Tasa de conversión desactivada: dependía del tracking de vistas
        (benefit_view), que ya no se registra. Sin esos datos mostraba 0%.
      <KpiCard
        label="Tasa de conversión"
        value={conversionRateLabel ?? "—"}
        description="Usuarios que abrieron el detalle y luego canjearon"
      /> */}
  </div>
);
